export class ServiceOrder {
  constructor(
    public id: string,
    public reference: string,
    public user: User,
    public status: string,
    public installation: Installation,
    public priority: string,
    public createdAt: string | null,
    public assignee: User | null,
    /** Defeitos informados (mesmo campo que o CSV de lote manda como defeitos_informados) **/
    public problems: string | null = null,
    /** Observação do call center (CSV de lote: observacao) **/
    public notes: string | null = null,
    public closeNotes: string | null = null,
    public startedAt: string | null = null,
    public closedAt: string | null = null,
    public chAlertId: string | null = null,
  ) { }
}

export class User {
  constructor(
    public id: string | null,
    /** Login (ex.: app_mao). Fica como fallback e chave de busca; a tela mostra o nome **/
    public username: string | null,
    public firstName: string | null = null,
    public lastName: string | null = null,
  ) { }

  /** Nome para exibição: "Nome Sobrenome", ou o login quando o cadastro não tem nome **/
  get displayName(): string | null {
    return userDisplayName(this);
  }
}

/**
 * Nome de exibição tolerante a objeto simples (dados antigos em memória, valores do formulário):
 * nome completo, senão login, senão null.
 **/
export function userDisplayName(user: Partial<User> | null | undefined): string | null {
  if (!user) {
    return null;
  }

  const name = [user.firstName, user.lastName].map(part => part?.trim()).filter(Boolean).join(' ');

  return name || user.username || null;
}

/** Converte o node de usuário (OS ou busca) para o modelo da tela **/
export function mapUserNode(node: any): User {
  return new User(node?.id ?? null, node?.username ?? null, node?.firstName ?? null, node?.lastName ?? null);
}

/** Classe referente a instalação da ordem de serviço**/
/** Dados de contexto da instalação exibidos no painel ao escolher a instalação da OS **/
export interface InstallationDetails {
  /** "Rua X, 120" — rua e número já juntos **/
  street: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  isActive: boolean | null;
  equipmentType: string | null;
  gatewayReference: string | null;
  /** "LED 150W · Philips" **/
  lampType: string | null;
}

export class Installation {
  constructor(
    public id: string,
    public reference: string | null,
    /** O schema declara Float em latitude/longitude, o Apollo devolve number **/
    public latitude: number | null,
    public longitude: number | null,
    public deviceMac: string | null,
    /** Serial do equipamento instalado: é o que o operador lê na etiqueta em campo **/
    public serialNumber: string | null = null,
    public details: InstallationDetails | null = null,
  ) { }
}

/**
 * A OS devolve `installationId` como inteiro puro (alerts-api), mas toda consulta/mutation do
 * bifrost espera o ID relay da instalação: base64 de "Installation:<int>". Sem isso, salvar a
 * edição de uma OS sem re-selecionar a instalação caía em BFT_INVALID_DATA.
 **/
export function toInstallationGlobalId(id: string | number | null | undefined): string | null {
  if (id === null || id === undefined || id === '') {
    return null;
  }

  const raw = String(id);

  // Já é um ID relay (o autocomplete devolve assim): não recodifica
  if (!/^\d+$/.test(raw)) {
    return raw;
  }

  return btoa(`Installation:${raw}`);
}

/** Inverso de toInstallationGlobalId: ID relay -> inteiro do banco (é o `id` do map_nodes) **/
export function fromInstallationGlobalId(globalId: string | null | undefined): number | null {
  if (!globalId) {
    return null;
  }

  if (/^\d+$/.test(globalId)) {
    return Number(globalId);
  }

  try {
    const decoded = atob(globalId);
    const match = /^Installation:(\d+)$/.exec(decoded);

    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * ID relay do usuário (base64 de "UserType:<n>") -> id numérico como string. O filtro `assignee`
 * da consulta de OS é repassado cru ao alerts-api pelo bifrost em produção, que espera o número;
 * valor que não é um ID relay de usuário volta como veio.
 **/
export function toNumericUserId(globalId: string | null | undefined): string | null {
  if (!globalId) {
    return null;
  }

  try {
    const match = /^UserType:(\d+)$/.exec(atob(globalId));

    return match ? match[1] : globalId;
  } catch {
    return globalId;
  }
}

/** map_nodes usa a string 'NULL' quando a instalação não tem equipamento **/
export function geoText(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();

  return text && text.toUpperCase() !== 'NULL' ? text : null;
}

/**
 * Converte uma feature WFS da camada `map_nodes` do GeoServer para o modelo da tela. Mesma
 * fonte que a importação em lote usa. Campos que a camada não tem (cidade/UF, lâmpada) ficam
 * nulos. A `map_nodes` só traz `gateway_id`: a referência do gateway vem de uma segunda consulta
 * na camada `gateways` (ver `geoserverGatewayId` e `findInstallationOnGeoserver`).
 * `device_serial_gateway` só existe na camada `circuitbox`; fica como fallback se algum dia vier.
 **/
export function mapGeoserverFeature(feature: any): Installation | null {
  const props = feature?.properties;

  if (!props) {
    return null;
  }

  const coords = feature?.geometry?.coordinates;
  const isActive = props.is_active;

  return new Installation(
    toInstallationGlobalId(props.id) as string,
    geoText(props.reference),
    toFloat(coords?.[1]),
    toFloat(coords?.[0]),
    geoText(props.device_mac),
    geoText(props.device_serial_number),
    {
      street: geoText(props.street),
      district: geoText(props.district),
      city: geoText(props.city),
      state: geoText(props.state),
      isActive: isActive === true || isActive === false ? isActive
        : typeof isActive === 'string' ? isActive.toLowerCase() === 'true' : null,
      equipmentType: geoText(props.equipment_type),
      gatewayReference: geoText(props.device_serial_gateway),
      lampType: null,
    },
  );
}

/** `gateway_id` da feature de map_nodes como número, ou null **/
export function geoserverGatewayId(feature: any): number | null {
  const raw = feature?.properties?.gateway_id;
  const id = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);

  return Number.isFinite(id) ? id : null;
}

/** Referência do gateway a partir da feature da camada `gateways` (serial como fallback) **/
export function geoserverGatewayReference(response: any): string | null {
  const props = response?.features?.[0]?.properties;

  return geoText(props?.reference) ?? geoText(props?.device_serial_number);
}

function joinParts(parts: Array<string | null | undefined>, separator: string): string | null {
  const filled = parts.map(part => part?.toString().trim()).filter((part): part is string => !!part);

  return filled.length ? filled.join(separator) : null;
}

/** Converte o node da query `installation` para o modelo da tela **/
export function mapInstallationNode(node: any): Installation {
  const site = node?.site;
  const lamp = node?.lampType;

  return new Installation(
    node?.id,
    node?.reference ?? null,
    toFloat(site?.geoposition?.latitude),
    toFloat(site?.geoposition?.longitude),
    node?.device?.macAddress ?? null,
    node?.device?.serialNumber ?? null,
    {
      street: joinParts([site?.street, site?.number], ', '),
      district: site?.district ?? null,
      city: site?.city ?? null,
      state: site?.state ?? null,
      isActive: typeof node?.isActive === 'boolean' ? node.isActive : null,
      equipmentType: node?.device?.equipmentType?.reference ?? null,
      gatewayReference: node?.gateway?.reference ?? null,
      lampType: joinParts([lamp?.power ? `${lamp.power}W` : null, lamp?.model, lamp?.manufacturer], ' · '),
    },
  );
}

/**
 * Coerção defensiva na fronteira com a API: o schema declara Float, mas se vier string
 * numérica normaliza, porque GraphQLFloat rejeita string na coerção da variável.
 **/
export function toFloat(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Encurta a data já convertida por tzConvertUTC2Local para uso em tabela e timeline:
 * `08/05/2026, 18:22:40 (GMT-3)` vira `08/05/2026 18:22`. O valor completo continua
 * disponível no title do elemento.
 **/
/**
 * O alerts-api grava as datas em UTC, mas devolve ISO sem `Z` nem offset (coluna TIMESTAMP sem fuso).
 * `new Date("2026-09-25T19:19:48")` lê isso como hora local e a tela mostrava 3h a mais. Marca
 * como UTC antes da conversão de fuso do usuário; string que já tem offset passa intacta.
 **/
export function asUtcIso(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value) ? `${value}Z` : value;
}

export function shortDate(value: string | null): string {
  if (!value) {
    return '';
  }

  const match = value.match(/^(\d{2}\/\d{2}\/\d{4}),?\s+(\d{2}:\d{2})/);

  return match ? `${match[1]} ${match[2]}` : value;
}
